import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { authStorageAtom } from "apps/web/src/atoms";
import { useAtomValue } from "jotai";
import moment, { Moment } from "moment";
import React, { useState } from "react";

import {
  getLocalLocationList,
  setLocalLocationList
} from "../../../utils/localLocationList";

interface Props {
  visible: boolean;
  setVisibility: (visible: boolean) => void;
  onConfirm: (location: string, inTime?: string, outTime?: string) => void;
}

const PastPunchModal: React.FC<Props> = ({ visible, setVisibility, onConfirm }) => {
  const authStorage = useAtomValue(authStorageAtom);
  const { user } = authStorage;
  const isImmerxive = user?.company === "Immerxive";

  const [location, setLocation] = useState(isImmerxive ? "straordinario" : "ufficio");
  const [customLocation, setCustomLocation] = useState<string | undefined>(undefined);
  const [inTime, setInTime] = useState<Moment | null>(null);
  const [outTime, setOutTime] = useState<Moment | null>(null);
  const [locationList, setLocationListState] = useState(getLocalLocationList());

  const handleClose = () => setVisibility(false);

  const handleConfirm = () => {
    const effectiveLocation = location === "altro" ? (customLocation || "") : location;

    if (customLocation) {
      const lower = customLocation.toLocaleLowerCase();
      if (!locationList.includes(lower)) {
        const newList = [...locationList, lower];
        setLocationListState(newList);
        setLocalLocationList(newList);
      }
    }

    onConfirm(
      effectiveLocation,
      inTime ? inTime.format("HH:mm:ss.SSS") : undefined,
      outTime ? outTime.format("HH:mm:ss.SSS") : undefined
    );
  };

  const isConfirmDisabled =
    (location === "altro" && (!customLocation || customLocation === "")) ||
    (!inTime && !outTime);

  return (
    <Dialog open={visible} onClose={handleClose}>
      <DialogTitle>Timbratura passata</DialogTitle>
      <DialogContent sx={{ minWidth: 320 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La richiesta verrà inviata in approvazione.
        </Typography>
        <RadioGroup
          row
          value={location}
          onChange={(_, v) => {
            setLocation(v);
            setCustomLocation(undefined);
          }}
        >
          {isImmerxive ? (
            <FormControlLabel
              value="straordinario"
              control={<Radio />}
              label="Straordinario"
            />
          ) : (
            <>
              <FormControlLabel value="ufficio" control={<Radio />} label="Ufficio" />
              <FormControlLabel value="casa" control={<Radio />} label="Casa" />
              <FormControlLabel value="altro" control={<Radio />} label="Altro" />
            </>
          )}
        </RadioGroup>
        {location === "altro" && (
          <Autocomplete
            disablePortal
            freeSolo
            size="small"
            sx={{ mb: 2, mt: 1 }}
            options={locationList}
            value={customLocation}
            onChange={(_, v) => setCustomLocation(v || undefined)}
            onInputChange={(_, v) => setCustomLocation(v || undefined)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                placeholder="Scrivi dove stai lavorando"
              />
            )}
          />
        )}
        <Box display="flex" gap={2} mt={2}>
          <MobileTimePicker
            label="Entrata"
            value={inTime}
            onChange={setInTime}
            ampm={false}
          />
          <MobileTimePicker
            label="Uscita"
            value={outTime}
            onChange={setOutTime}
            ampm={false}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Annulla</Button>
        <Button onClick={handleConfirm} disabled={isConfirmDisabled} variant="contained">
          Invia in approvazione
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PastPunchModal;
